import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle2, Mail, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Round 95p·6 — Prefill from `?email=` when the user was bounced
  // here by the ResetPassword page after clicking a stale link, so
  // they don't have to retype it.
  const [email, setEmail] = useState(params.get('email') || '');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md bg-card border rounded-xl p-8 shadow-lg">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4"
          data-testid="forgot-password-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Dashboard
        </button>

        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-password-sent">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/15 ring-1 ring-emerald-500/30 dark:ring-emerald-400/40 shadow-lg dark:shadow-emerald-500/20">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-300" />
            </div>
            <h1 className="text-xl font-bold">Check your inbox</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If that email is registered, we&apos;ve sent a password reset link to{' '}
              <span className="font-semibold text-foreground">{email}</span>. The link expires in
              30 minutes and can only be used once.
            </p>
            <p className="text-xs text-muted-foreground">
              Emails usually arrive within a minute. If you don&apos;t see it, check your spam folder before requesting another.
            </p>
            <Link to="/" className="inline-block text-primary hover:underline text-sm font-medium">
              Return to home
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/10">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold leading-none">Forgot your password?</h1>
                <p className="text-xs text-muted-foreground mt-1">We&apos;ll email you a reset link.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="forgot-password-form">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                data-testid="forgot-password-email"
                className="h-10"
              />
              {error && <p className="text-destructive text-xs">{error}</p>}
              <Button
                type="submit"
                disabled={submitting || !email.trim()}
                className="w-full h-10 font-semibold"
                data-testid="forgot-password-submit"
              >
                {submitting ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>

            <p className="text-xs text-center text-muted-foreground mt-4">
              Remembered it?{' '}
              <button
                type="button"
                onClick={() => {
                  try { sessionStorage.setItem('open_login_modal', '1'); } catch { /* noop */ }
                  navigate('/');
                }}
                className="text-primary hover:underline font-medium"
                data-testid="forgot-password-back-to-signin"
              >
                Back to sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
